import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { useProducts } from "../context/ProductsContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const currency = (n) => `$${n.toFixed(2)}`;

export default function FeaturedCollection() {
  const { add } = useCart();
  const { products } = useProducts();

  return (
    <section
      data-testid="featured-collection"
      className="relative bg-[color:var(--sr-bg)] py-24 md:py-40"
    >
      <div className="sr-container">
        <div className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.6 }}
              className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> The Collection
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8 }}
              className="font-serif text-5xl md:text-7xl lg:text-[6.5rem] leading-[0.92] tracking-tight text-[color:var(--sr-primary)]"
            >
              Five bottles.
              <br />
              <em className="italic font-light text-[color:var(--sr-accent)]">
                One quiet philosophy.
              </em>
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="col-span-12 md:col-span-4 md:col-start-9 text-[color:var(--sr-text)]/75 leading-relaxed"
          >
            <p>
              Each bottle is a study in restraint: ingredients chosen slowly,
              pressed carefully, and finished by hand. No shortcuts. Nothing
              added for effect.
            </p>
          </motion.div>
        </div>

        {/* 3 + 2 grid */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 md:gap-8">
          {products.map((p, idx) => (
            <ProductCard
              key={p.id}
              product={p}
              idx={idx}
              onAdd={() => {
                add(
                  {
                    id: p.id,
                    lookup_key: p.lookup_key,
                    name: p.name,
                    price: p.price,
                    image: p.image,
                  },
                  1
                );
                toast.success(`${p.name} added to bag`);
              }}
            />
          ))}
        </ul>
      </div>
    </section>
  );
}

const LAYOUTS = [
  // Row 1 — three cards
  "col-span-12 sm:col-span-1 lg:col-span-2",
  "col-span-12 sm:col-span-1 lg:col-span-2",
  "col-span-12 sm:col-span-1 lg:col-span-2",
  // Row 2 — two cards, centered under the row above
  "col-span-12 sm:col-span-1 lg:col-span-2 lg:col-start-2",
  "col-span-12 sm:col-span-1 lg:col-span-2 lg:col-start-4",
];

function ProductCard({ product, idx, onAdd }) {
  const layout = LAYOUTS[idx % LAYOUTS.length];
  return (
    <motion.li
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      className={`${layout} group`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-[color:var(--sr-secondary)]">
          <span
            className="absolute z-10 top-4 left-4 label-eyebrow text-[color:var(--sr-primary)]/60"
          >
            N°{product.number}
          </span>
          <motion.img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1.06 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.04 }}
          />
          <button
            type="button"
            data-testid={`quick-add-${product.id}`}
            onClick={(e) => {
              e.preventDefault();
              onAdd();
            }}
            className="absolute bottom-4 right-4 flex items-center gap-2 px-4 py-3 min-h-[44px] bg-[color:var(--sr-bg)] text-[color:var(--sr-primary)] label-eyebrow md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-all duration-500"
            aria-label={`Add ${product.name} to bag`}
          >
            <span>Add to bag</span>
            <Plus size={14} strokeWidth={1.5} />
          </button>
        </div>
        <div className="mt-5 flex items-baseline justify-between gap-4">
          <p className="font-serif text-2xl md:text-3xl leading-tight text-[color:var(--sr-primary)] group-hover:text-[color:var(--sr-accent)] transition-colors">
            {product.name}
          </p>
          <div className="text-right shrink-0">
            <p className="font-serif text-xl text-[color:var(--sr-primary)] tabular-nums">
              {currency(product.price)}
            </p>
            <p className="label-eyebrow text-[color:var(--sr-muted)] mt-2 inline-flex items-center gap-1">
              View <ArrowUpRight size={12} strokeWidth={1.25} />
            </p>
          </div>
        </div>
      </Link>
    </motion.li>
  );
}
